import type { VehicleOrder2Schemas } from './index'
import _ from 'lodash'

export function getVehicleOrderInvoice(
  vehicleOrder: VehicleOrder2Schemas.vehicleOrderHeadersModel
): VehicleOrder2Schemas.VehicleOrderInvoice {
  const invoiceLines = vehicleOrder.invoiceLines

  const allVehicles = vehicleOrder.holdingTerms.flatMap((holdingTerm) =>
    _.uniq(
      holdingTerm.lineItems
        .map((l) => l.modelNumber)
        .map((modelNumber) => ({
          modelNumber,
          holdingTerm,
          quantity: _.sum(holdingTerm.lineItems.filter((l) => l.modelNumber === modelNumber).map((l) => l.quantity))
        }))
    )
  )

  const vehicles = allVehicles.map((vehicle) => {
    const paymentTerms = vehicle.holdingTerm.paymentTerms.map((paymentTerm) => {
      const vehiclePayments = invoiceLines.filter(
        (v) =>
          v.holdingTermId === vehicle.holdingTerm.id &&
          v.modelNumber === vehicle.modelNumber &&
          v.paymentTerm === paymentTerm
      )

      return {
        paymentTerm,
        amount: _.sum(vehiclePayments.map((v) => v.unitCost * v.quantity)),
        taxAmount: _.sum(vehiclePayments.map((v) => v.unitTaxAmount * v.quantity))
      }
    })
    const subtotal = _.sum(paymentTerms.map((p) => p.amount))

    return {
      holdingTerm: vehicle.holdingTerm,
      modelNumber: vehicle.modelNumber,
      quantity: vehicle.quantity,
      taxAmount: _.sum(paymentTerms.map((p) => p.taxAmount)),
      paymentTerms: paymentTerms,
      subtotal: subtotal
    }
  })

  const discounts = vehicleOrder.discounts.map((discount) => {
    const discountPayments = invoiceLines.filter((i) => i.discountId === discount.id)
    return {
      discount: discount,
      paymentTerms: discountPayments.map((d) => ({
        paymentTerm: d.paymentTerm,
        amount: d.unitCost * d.quantity
      })),
      total: _.sum(discountPayments.map((p) => p.unitCost * p.quantity))
    }
  })
  const addons = vehicleOrder.addons.map((addon) => {
    const addonPayments = invoiceLines.filter((i) => i.addonId === addon.id)
    return {
      addon: addon,
      paymentTerms: addonPayments.map((d) => ({
        paymentTerm: d.paymentTerm,
        amount: d.unitCost * d.quantity
      })),
      total: _.sum(addonPayments.map((p) => p.unitCost * p.quantity))
    }
  })

  const allPaymentTerms = _.uniq(invoiceLines.map((i) => i.paymentTerm))

  const paymentTerms = allPaymentTerms.map((paymentTerm) => ({
    paymentTerm: paymentTerm,
    total: _.sum(
      invoiceLines.filter((i) => i.paymentTerm === paymentTerm).map((p) => (p.unitCost + p.unitTaxAmount) * p.quantity)
    ),
    taxAmount: _.sum(invoiceLines.filter((i) => i.paymentTerm === paymentTerm).map((p) => p.unitTaxAmount * p.quantity)),
    subtotal: _.sum(invoiceLines.filter((i) => i.paymentTerm === paymentTerm).map((p) => p.unitCost * p.quantity)),
    vehicles: invoiceLines
      .filter((i) => i.paymentTerm === paymentTerm && i.modelNumber)
      .map((v) => {
        const holdingTerm = vehicleOrder.holdingTerms.find((holdingTerm) => holdingTerm.id === v.holdingTermId)!
        return {
          holdingTerm: holdingTerm,
          amount: v.unitCost * v.quantity,
          quantity: v.quantity,
          modelNumber: v.modelNumber!
        }
      }),
    addons: invoiceLines
      .filter((i) => i.paymentTerm === paymentTerm && i.addonId)
      .map((s) => {
        const match = vehicleOrder.addons.find((addon) => addon.id === s.addonId)!
        return { addon: match, amount: s.unitCost * s.quantity }
      }),
    discounts: invoiceLines
      .filter((i) => i.paymentTerm === paymentTerm && i.discountId)
      .map((s) => {
        const match = vehicleOrder.discounts.find((discount) => discount.id === s.discountId)!
        return { discount: match, amount: s.unitCost * s.quantity }
      })
  }))

  const subtotal = _.sum([
    ...vehicles.map((s) => s.subtotal),
    ...addons.map((s) => s.total),
    ...discounts.map((s) => s.total)
  ])

  const taxAmount = _.sum(invoiceLines.map((s) => s.unitTaxAmount * s.quantity))
  const total = taxAmount + subtotal

  return {
    vehicles,
    addons,
    discounts,
    subtotal,
    taxAmount,
    total,
    payments: paymentTerms
  }
}
